import { useEffect, useRef } from 'react';

const FIELD_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];
// Enter on a focused button or link already activates it: a shortcut on top
// would act twice (« Fermer » then « Confirmer »).
const ACTIVABLE_TAGS = ['BUTTON', 'A'];
// Marks the choices of a qualification (results, moments, third-attempt
// actions): a choice is made by a click (or Space), so Enter on one confirms,
// as it does once focus left it.
export const ENTER_CONFIRMS_ATTRIBUTE = 'data-enter-confirms';

const isChoice = (target) =>
  typeof target?.closest === 'function' &&
  Boolean(target.closest(`[${ENTER_CONFIRMS_ATTRIBUTE}]`));

const isField = (target) =>
  Boolean(target) &&
  (FIELD_TAGS.includes(target.tagName) || target.isContentEditable);

const shortcutOf = (event) => {
  if (event.key === 'Enter') return 'enter';
  const key = String(event.key || '').toLowerCase();
  return key === 'a' || key === 's' ? key : null;
};

/**
 * Keyboard shortcuts of the call sheet, listened on the whole page while
 * `enabled`: `handlers` = { a, s, enter }, each optional. Nothing happens in
 * a field, with Cmd, Ctrl or Alt, or on a key held down; Enter does nothing on
 * a focused button or link, except a choice marked ENTER_CONFIRMS_ATTRIBUTE
 * (its own activation is then prevented).
 */
const useCallSheetShortcuts = (handlers, enabled = true) => {
  // The latest handlers, so that the listener is not set again on each render.
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (isField(target)) return;
      const shortcut = shortcutOf(event);
      const handler = shortcut && latest.current?.[shortcut];
      if (!handler) return;
      if (
        shortcut === 'enter' &&
        ACTIVABLE_TAGS.includes(target?.tagName) &&
        !isChoice(target)
      ) {
        return;
      }
      event.preventDefault();
      handler();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
};

export default useCallSheetShortcuts;
